// Owner console → Overview (SYSTEM-001, owner 2026-10-25; D-065, laid out like the vendor dashboard's
// Today page in D-066): a greeting, KPI cards with a trend line (shops, active this week, monthly plan
// revenue, what Adyvance earned from wallets), quick links, what needs staff now (each links to the
// section that handles it), renewals, shops by plan, sign-ups and earnings over time, and the latest
// staff actions. Totals only (IA §3 privacy).
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import type { ConsoleOverview } from "@ady/contracts";
import { formatDateAD, formatDateTime, formatMoney } from "@ady/format";
import { i18n, t } from "@ady/i18n";
import { Banner, Chart, cx, Icon, Sparkline, StatusBadge, useWidth, type IconName } from "@ady/ui";
import { api, errorMessage, vendorsConsoleApi } from "./api";
import { reasonText } from "./health";
import { card, dayLabel, monthLabel } from "./ui";

export const OVERVIEW_KEY = ["console", "overview"] as const;
const money = (p: number) => formatMoney(p);
const n = (v: number) => v.toLocaleString("en-IN");

const ATTENTION_ICON: Record<string, IconName> = {
  topups: "wallet",
  kyc: "shieldCheck",
  import_payments: "ship",
  import_requests: "ship",
  sms: "chat",
  ads: "megaphone",
  tickets: "lifebuoy",
  ideas: "sparkle",
  at_risk: "pulse",
  jobs: "jobs",
};

const QUICK: [string, string, IconName, string][] = [
  ["vendors", "/vendors", "store", "console.nav.vendors"],
  ["plans", "/plans", "crown", "console.nav.plans"],
  ["billing", "/billing", "wallet", "console.nav.billing"],
  ["import", "/import", "ship", "importMarket.console.title"],
  ["support", "/support", "lifebuoy", "console.nav.support"],
];

function nptHour(now = new Date()): number {
  return Number(
    new Intl.DateTimeFormat("en-GB", {
      hour: "numeric",
      hourCycle: "h23",
      timeZone: "Asia/Kathmandu",
    }).format(now),
  );
}
const greetingKey = (h: number) => (h < 12 ? "morning" : h < 17 ? "afternoon" : "evening");

export function OverviewConsole() {
  const q = useQuery({
    queryKey: OVERVIEW_KEY,
    queryFn: () => vendorsConsoleApi.overview(),
    retry: false,
    refetchInterval: 60_000,
  });
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, retry: false });
  const router = useRouter();
  const go = (href: string) => router.history.push(href);
  const o = q.data;
  const name =
    (me.data?.staff.display_name ?? "").trim().split(/\s+/)[0] ||
    me.data?.staff.email.split("@")[0] ||
    "";
  return (
    <section className="flex flex-col gap-4" data-testid="console-overview">
      <header className="flex flex-col">
        <h2 className="text-lg leading-7 font-bold">
          {i18n.tDynamic(
            `plans.overview.greeting.${greetingKey(nptHour())}`,
            "plans.overview.title",
            { name },
          )}
        </h2>
        <p className="text-xs text-fg">
          {t("plans.overview.statsFor")}{" "}
          <span className="ady-text-brand font-bold">{formatDateAD(new Date(), "medium")}</span>
        </p>
      </header>
      {q.isError ? <Banner kind="error" title={errorMessage(q.error)} /> : null}
      {!o ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={cx(card, "h-42 animate-pulse")} />
          ))}
        </div>
      ) : (
        <Body o={o} go={go} />
      )}
    </section>
  );
}

function KpiCard({
  title,
  sub,
  value,
  money: isMoney,
  spark,
  variant,
  foot,
  onClick,
}: {
  title: string;
  sub: string;
  value: number;
  money?: boolean;
  spark: number[];
  variant: "line" | "dots" | "gradient";
  foot?: string;
  onClick?: () => void;
}) {
  return (
    <section className={cx(card, "flex min-h-42 flex-col px-4 pt-3.5 pb-3")} aria-label={title}>
      <header className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{title}</h3>
        {onClick ? (
          <button
            type="button"
            onClick={onClick}
            className="rounded-full bg-sunken px-2.5 py-0.5 text-2xs font-medium text-fg hover:bg-hover"
          >
            <Icon name="chevronRight" className="size-3" />
            <span className="ady-sr-only">{title}</span>
          </button>
        ) : null}
      </header>
      <p className="pt-2 text-2xs text-fg-tertiary">{sub}</p>
      <p className="ady-ledger flex items-baseline gap-1 pt-2">
        {isMoney ? (
          <span className="text-xs font-medium text-fg-tertiary">{t("today.home.currency")}</span>
        ) : null}
        <span className="text-title-md leading-8 font-semibold">
          {isMoney ? formatMoney(value, { bare: true }) : n(value)}
        </span>
      </p>
      {foot ? <p className="text-2xs text-fg-tertiary">{foot}</p> : null}
      <div className="mt-auto px-3 pt-4">
        <Sparkline values={spark} variant={variant} label={title} />
      </div>
    </section>
  );
}

function ChartCard({
  title,
  labels,
  values,
  series,
}: {
  title: string;
  labels: string[];
  values: number[];
  series: string;
}) {
  const [ref, width] = useWidth<HTMLDivElement>(520);
  return (
    <section className={cx(card, "flex flex-col gap-2 p-4")}>
      <h3 className="text-sm font-semibold">{title}</h3>
      <div ref={ref} className="w-full">
        <Chart
          kind="bars"
          groups={[values]}
          labels={labels}
          showLabel={(i) =>
            labels.length <= 8 || i === labels.length - 1 || (i % 5 === 0 && i < labels.length - 3)
          }
          series={[series]}
          label={title}
          width={Math.max(240, width)}
          height={200}
        />
      </div>
    </section>
  );
}

function Body({ o, go }: { o: ConsoleOverview; go: (href: string) => void }) {
  const lc = o.shops.by_lifecycle;
  const maxPlan = Math.max(1, ...o.plans.map((p) => p.shops));
  let run = o.shops.total - o.signups.reduce((s, d) => s + d.n, 0);
  const shopsLine = o.signups.map((d) => (run += d.n));
  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="overview-kpis">
        <KpiCard
          title={t("plans.overview.shops")}
          sub={t("plans.overview.subs.shops")}
          value={o.shops.total}
          spark={shopsLine}
          variant="line"
          foot={`${t("plans.overview.newMonth")}: ${n(o.shops.new_30d)} · ${t("plans.overview.lifecycle.suspended")}: ${n(lc.suspended)}`}
          onClick={() => go("/vendors")}
        />
        <KpiCard
          title={t("plans.overview.activeWeek")}
          sub={t("plans.overview.subs.active")}
          value={o.shops.active_7d}
          spark={o.signups.map((d) => d.n)}
          variant="dots"
          foot={`${Math.round((o.shops.active_7d / Math.max(1, o.shops.total)) * 100)}%`}
        />
        <KpiCard
          title={t("plans.overview.mrr")}
          sub={t("plans.overview.subs.mrr")}
          value={o.money.mrr_paisa}
          money
          spark={o.plans.map((p) => p.mrr_paisa)}
          variant="gradient"
          foot={`${t("plans.overview.paying")}: ${n(o.shops.paying)}`}
          onClick={() => go("/plans")}
        />
        <KpiCard
          title={t("plans.overview.earned")}
          sub={t("plans.overview.subs.earned")}
          value={o.money.earned_month_paisa}
          money
          spark={o.earned.map((e) => e.paisa)}
          variant="line"
          foot={t("plans.overview.earnedVs", { amount: money(o.money.earned_last_month_paisa) })}
          onClick={() => go("/billing")}
        />
      </div>

      {/* Quick links, as on the vendor dashboard */}
      <nav aria-label={t("console.shell.sections")}>
        <ul className="grid grid-cols-2 gap-4 md:grid-cols-5">
          {QUICK.map(([id, path, icon, label]) => (
            <li key={id}>
              <a
                href={path}
                onClick={(e) => {
                  e.preventDefault();
                  go(path);
                }}
                className={cx(
                  card,
                  "flex h-full flex-col gap-2 p-4 text-fg no-underline hover:bg-hover",
                )}
              >
                <span className="flex size-10 flex-none items-center justify-center rounded-xs border border-card bg-surface">
                  <Icon name={icon} gradient />
                </span>
                <span className="ady-hover-label text-sm font-semibold">
                  {i18n.tDynamic(label, "console.nav.overview")}
                </span>
                <span className="text-xs text-fg-secondary">
                  {i18n.tDynamic(`plans.overview.quick.${id}`, "plans.overview.lead")}
                </span>
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <div className="grid gap-4 lg:grid-cols-3">
        <section
          className={cx(card, "flex flex-col gap-2 p-4 lg:col-span-2")}
          data-testid="overview-attention"
        >
          <h3 className="text-sm font-semibold">{t("plans.overview.attentionTitle")}</h3>
          {o.attention.length === 0 ? (
            <p className="flex items-center gap-2 text-sm text-fg-secondary">
              <span className="text-success-fg">
                <Icon name="checkCircle" />
              </span>
              {t("plans.overview.attentionNone")}
            </p>
          ) : (
            <ul className="grid gap-2 sm:grid-cols-2">
              {o.attention.map((a) => (
                <li key={a.kind}>
                  <button
                    type="button"
                    onClick={() => go(a.path)}
                    className="flex w-full items-center gap-2.5 rounded-xs border border-card px-2.5 py-2 text-start hover:bg-hover"
                  >
                    <span className="flex size-8 flex-none items-center justify-center rounded-xs bg-warning-bg text-warning-fg">
                      <Icon name={ATTENTION_ICON[a.kind] ?? "inbox"} />
                    </span>
                    <span className="flex-1 text-xs font-medium">
                      {i18n.tDynamic(
                        `plans.overview.attention.${a.kind}`,
                        "plans.overview.attentionTitle",
                      )}
                    </span>
                    <span className="rounded-full bg-warning-bg px-2 text-xs font-bold text-warning-fg tabular-nums">
                      {n(a.count)}
                    </span>
                    <Icon name="chevronRight" className="text-fg-tertiary" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-1 grid grid-cols-2 gap-3 border-t border-subtle pt-3 sm:grid-cols-4">
            {(
              [
                ["renewSoon", o.renewals.next_7d, false],
                ["renewOff", o.renewals.auto_renew_off, o.renewals.auto_renew_off > 0],
                ["renewShort", o.renewals.short, o.renewals.short > 0],
                ["inGrace", o.renewals.in_grace, o.renewals.in_grace > 0],
              ] as const
            ).map(([k, v, warn]) => (
              <span key={k} className="flex flex-col">
                <span className="text-2xs text-fg-tertiary">{t(`plans.overview.${k}`)}</span>
                <span
                  className={cx(
                    "text-title-sm font-semibold tabular-nums",
                    warn && "text-warning-fg",
                  )}
                >
                  {n(v)}
                </span>
              </span>
            ))}
          </div>
        </section>

        <section className={cx(card, "flex flex-col gap-2 p-4")}>
          <h3 className="text-sm font-semibold">{t("plans.overview.plansTitle")}</h3>
          <ul className="flex flex-col gap-2.5">
            {o.plans.map((p) => (
              <li key={p.plan_id} className="flex flex-col gap-1">
                <span className="flex items-center gap-2 text-xs">
                  <span className="flex size-6 flex-none items-center justify-center rounded-full ady-bg-brand-tint">
                    <Icon name={p.icon} gradient className="size-3.5" />
                  </span>
                  <span className="flex-1 font-medium">{p.name}</span>
                  <span className="font-semibold tabular-nums">{n(p.shops)}</span>
                  {p.mrr_paisa ? (
                    <span className="text-2xs text-fg-tertiary tabular-nums">
                      {money(p.mrr_paisa)}
                    </span>
                  ) : null}
                </span>
                <span className="h-1.5 overflow-hidden rounded-full bg-sunken">
                  <span
                    className="block h-full ady-bg-brand"
                    style={{ width: `${(p.shops / maxPlan) * 100}%` }}
                  />
                </span>
              </li>
            ))}
          </ul>
          <span className="mt-auto flex flex-wrap gap-1.5 pt-2">
            {(Object.keys(lc) as (keyof typeof lc)[])
              .filter((k) => lc[k] > 0)
              .map((k) => (
                <span key={k} className="inline-flex items-center gap-1">
                  <StatusBadge domain="vendor" status={k} />
                  <span className="text-2xs font-semibold tabular-nums">{n(lc[k])}</span>
                </span>
              ))}
          </span>
        </section>
      </div>

      <HealthCard o={o} go={go} />

      <div className="grid gap-4 lg:grid-cols-3">
        <ChartCard
          title={t("plans.overview.signupsTitle")}
          labels={o.signups.map((s) => dayLabel(s.day))}
          values={o.signups.map((s) => s.n)}
          series={t("plans.overview.series.shops")}
        />
        <ChartCard
          title={t("plans.overview.earnedTitle")}
          labels={o.earned.map((e) => monthLabel(e.month))}
          values={o.earned.map((e) => Math.round(e.paisa / 100))}
          series={t("plans.overview.series.earned")}
        />
        <section className={cx(card, "flex flex-col gap-2 p-4")}>
          <h3 className="text-sm font-semibold">{t("plans.overview.recentTitle")}</h3>
          {o.recent.length === 0 ? (
            <p className="text-xs text-fg-tertiary">{t("plans.overview.recentNone")}</p>
          ) : (
            <ul className="flex flex-col divide-y divide-subtle text-xs">
              {o.recent.slice(0, 8).map((r, i) => (
                <li key={i} className="flex justify-between gap-2 py-1.5">
                  <span className="truncate">
                    <span className="font-medium">{r.org_name ?? "—"}</span> ·{" "}
                    {r.action.replace(/[._]/g, " ")}
                  </span>
                  <span className="flex-none text-2xs text-fg-tertiary">
                    {r.at ? formatDateTime(r.at) : ""}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-auto pt-2 text-2xs text-fg-tertiary">
            {t("plans.overview.held")}:{" "}
            <span className="font-semibold text-fg">{money(o.money.held_paisa)}</span> ·{" "}
            {t("plans.overview.topupsWaiting", { n: o.money.topups_waiting })}
          </p>
        </section>
      </div>
    </>
  );
}

/** D-067: shops by health band (a stacked bar) and the lowest scores with what pulls them down. */
function HealthCard({ o, go }: { o: ConsoleOverview; go: (path: string) => void }) {
  const h = o.health;
  const total = Math.max(1, h.healthy + h.watch + h.at_risk);
  const bands = [
    ["healthy", h.healthy, "bg-success-fg"],
    ["watch", h.watch, "bg-warning-fg"],
    ["at_risk", h.at_risk, "bg-danger-fg"],
  ] as const;
  return (
    <section className={cx(card, "grid gap-4 p-4 lg:grid-cols-3")} data-testid="overview-health">
      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold">{t("desk.health.overview")}</h3>
        <span className="flex h-2.5 overflow-hidden rounded-full bg-sunken">
          {bands.map(([k, v, cls]) =>
            v ? <span key={k} className={cls} style={{ width: `${(v / total) * 100}%` }} /> : null,
          )}
        </span>
        <span className="grid grid-cols-3 gap-2">
          {bands.map(([k, v, cls]) => (
            <span key={k} className="flex flex-col">
              <span className="flex items-center gap-1 text-2xs text-fg-tertiary">
                <span className={cx("size-2 rounded-full", cls)} />
                {t(`desk.health.bands.${k}`)}
              </span>
              <span className="text-title-sm font-semibold tabular-nums">{n(v)}</span>
            </span>
          ))}
        </span>
        <p className="text-2xs text-fg-tertiary">{t("desk.health.how")}</p>
      </div>
      <div className="flex flex-col gap-2 lg:col-span-2">
        <span className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">{t("desk.health.lowest")}</h3>
          {h.at_risk ? (
            <button
              type="button"
              onClick={() => go("/vendors?flag=at_risk")}
              className="text-xs font-semibold text-link hover:underline"
            >
              {t("desk.health.filterAtRisk")}
            </button>
          ) : null}
        </span>
        {h.lowest.length === 0 ? (
          <p className="flex items-center gap-2 text-sm text-fg-secondary">
            <span className="text-success-fg">
              <Icon name="checkCircle" />
            </span>
            {t("desk.health.allHealthy")}
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-subtle">
            {h.lowest.map((x) => (
              <li key={x.org_id}>
                <button
                  type="button"
                  onClick={() => go(`/vendors/${x.org_id}`)}
                  className="flex w-full items-center gap-2.5 py-1.5 text-start hover:bg-hover"
                >
                  <span
                    className={cx(
                      "flex size-8 flex-none items-center justify-center rounded-full text-xs font-bold tabular-nums",
                      x.score < 40
                        ? "bg-danger-bg text-danger-fg"
                        : "bg-warning-bg text-warning-fg",
                    )}
                  >
                    {x.score}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-xs font-semibold">{x.name}</span>
                    <span className="truncate text-2xs text-fg-tertiary">
                      {x.reasons.map(reasonText).join(" · ")}
                    </span>
                  </span>
                  <Icon name="chevronRight" className="text-fg-tertiary" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
